import sbd from 'sbd';

export interface ExtractedContext {
  beforeContext: string | null;
  afterContext: string | null;
  phrase: string;
}

/**
 * Extract sentence context around a text selection.
 *
 * @param text - The full document text (plain text)
 * @param startOffset - Start character offset of selection
 * @param endOffset - End character offset of selection
 * @returns Context object with before/after sentences and the selected phrase
 */
export function extractContext(
  text: string,
  startOffset: number,
  endOffset: number
): ExtractedContext {
  const sentences = sbd.sentences(text, { preserve_whitespace: true });

  let currentPos = 0;
  let beforeContext: string | null = null;
  let afterContext: string | null = null;
  let foundStart = false;
  let sentenceIndex = 0;

  for (let i = 0; i < sentences.length; i++) {
    const sentence = sentences[i];
    const sentenceEnd = currentPos + sentence.length;

    if (!foundStart && sentenceEnd > startOffset) {
      // This sentence contains the start - grab previous sentence
      beforeContext = i > 0 ? sentences[i - 1].trim() : null;
      foundStart = true;
      sentenceIndex = i;
    }

    if (foundStart && currentPos >= endOffset) {
      // This sentence is after our selection
      afterContext = sentence.trim();
      break;
    }

    currentPos = sentenceEnd;
  }

  // If we didn't find an after context, try to grab the next sentence
  if (foundStart && !afterContext && sentenceIndex < sentences.length - 1) {
    afterContext = sentences[sentenceIndex + 1].trim();
  }

  const phrase = text.slice(startOffset, endOffset);

  return {
    beforeContext,
    afterContext,
    phrase,
  };
}

export interface ContainingSentenceInfo {
  sentence: string;
  sentenceStart: number;
  sentenceEnd: number;
}

/**
 * Return the sentence that contains the given selection offsets, with its start/end
 * character indices relative to the full text.
 */
export function getContainingSentenceInfo(
  text: string,
  startOffset: number,
  endOffset: number
): ContainingSentenceInfo | null {
  if (startOffset < 0 || endOffset < 0 || endOffset < startOffset) return null;

  const sentences = sbd.sentences(text, { preserve_whitespace: true });
  let currentPos = 0;

  for (let i = 0; i < sentences.length; i++) {
    const sentence = sentences[i];
    const sentenceStart = currentPos;
    const sentenceEnd = currentPos + sentence.length;

    if (sentenceEnd > startOffset && sentenceStart <= startOffset) {
      // Selection starts within this sentence; treat as the containing sentence
      return {
        sentence: sentence.trim(),
        sentenceStart,
        sentenceEnd,
      };
    }

    currentPos = sentenceEnd;
  }

  return null;
}

/**
 * Get the plain text from TipTap JSONContent.
 *
 * @param bodyJson - The TipTap document JSON
 * @returns Plain text string
 */
interface TipTapNode {
  type?: string;
  text?: string;
  content?: TipTapNode[];
}

export function getPlainTextFromTipTap(bodyJson: Record<string, unknown> | null): string {
  if (!bodyJson) return '';

  function extractText(node: TipTapNode): string {
    if (node.type === 'text') {
      return node.text || '';
    }

    if (node.content && Array.isArray(node.content)) {
      const text = node.content.map(extractText).join('');
      // Block elements add newline after their content (matches backend tiptap_to_plain_text)
      if (node.type === 'paragraph' || node.type === 'heading' || node.type === 'blockquote' || node.type === 'code_block') {
        return text + '\n';
      }
      return text;
    }

    return '';
  }

  return extractText(bodyJson as TipTapNode);
}
